class StickyATC extends HTMLElement {
  connectedCallback() {
    this.submitBtn = this.querySelector('[data-sticky-submit]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.compareEl = this.querySelector('[data-sticky-compare]');

    // Main CTA button on PDP
    // 1) Prefer a custom trigger for special templates (e.g. "Bundle konfigurieren")
    //    -> add data-sticky-trigger="true" to the desired button in that template
    // 2) Fallback to Dawn default add-to-cart selectors
    this.mainSubmit =
      document.querySelector('[data-sticky-trigger="true"]') ||
      document.querySelector('product-form form button[name="add"]') ||
      document.querySelector('form[action*="/cart/add"] button[type="submit"]') ||
      document.querySelector('button[name="add"]');

    if (!this.mainSubmit) return;

    // Click sticky button -> click main CTA (keeps Dawn cart drawer/ajax logic)
    if (this.submitBtn) {
      this.onStickyClick = () => this.mainSubmit.click();
      this.submitBtn.addEventListener('click', this.onStickyClick);
    }

    // Update price + availability on variant change (Dawn event)
    this.onVariantChange = (evt) => {
      const v = evt?.detail?.variant;
      if (!v) return;

      // If bundle mode is active, let bundle observer control price/title
      if (this.isBundleMode) return;

      if (this.priceEl) this.priceEl.textContent = this.formatMoney(v.price);

      if (this.compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          this.compareEl.hidden = false;
          this.compareEl.textContent = this.formatMoney(v.compare_at_price);
        } else {
          this.compareEl.hidden = true;
          this.compareEl.textContent = '';
        }
      }

      if (this.submitBtn) this.submitBtn.disabled = !v.available;
    };

    document.addEventListener('variant:change', this.onVariantChange);

    // --- Bundle mode: use configurator totals if present on page ---
    this.bundlePayEl = document.querySelector('[data-bundle-total-pay]');
    this.bundleRegularEl = document.querySelector('[data-bundle-total-regular]');
    this.bundleListEl = document.querySelector('[data-summary-list]');
    this.bundleLineEl = this.querySelector('[data-sticky-bundle]'); // added in sticky-atc.liquid
    this.titleEl = this.querySelector('.sticky-atc__title');

    this.isBundleMode = !!this.bundlePayEl;

    this.updateFromBundle = () => {
      if (!this.bundlePayEl) return;

      // Title
      if (this.titleEl) this.titleEl.textContent = 'Dein Bundle';

      // "X Artikel ausgewählt" line
      if (this.bundleLineEl) {
        const count = this.bundleListEl ? this.bundleListEl.querySelectorAll('li').length : 0;
        this.bundleLineEl.hidden = false;
        this.bundleLineEl.textContent = count > 0 ? `${count} Artikel ausgewählt` : 'Bundle zusammenstellen';
      }

      // Price (already formatted in DOM)
      const payText = (this.bundlePayEl.textContent || '').trim();
      if (payText && this.priceEl) this.priceEl.textContent = payText;

      // Compare / regular (show only if not "–")
      if (this.compareEl) {
        const regText = this.bundleRegularEl ? (this.bundleRegularEl.textContent || '').trim() : '';
        if (regText && regText !== '–' && regText !== '-') {
          this.compareEl.hidden = false;
          this.compareEl.textContent = regText;
        } else {
          this.compareEl.hidden = true;
          this.compareEl.textContent = '';
        }
      }
    };

    if (this.isBundleMode) {
      // Initial bundle render
      this.updateFromBundle();

      // Observe changes in totals + selection list
      this.bundleObserver = new MutationObserver(() => this.updateFromBundle());

      this.bundleObserver.observe(this.bundlePayEl, {
        childList: true,
        characterData: true,
        subtree: true
      });

      if (this.bundleRegularEl) {
        this.bundleObserver.observe(this.bundleRegularEl, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      if (this.bundleListEl) {
        this.bundleObserver.observe(this.bundleListEl, {
          childList: true,
          subtree: true
        });
      }
    }

    // --- Visibility logic ---
    // Show sticky only when:
    // - main CTA is NOT visible
    // - footer is NOT visible
    // - (mobile only) product media/gallery is NOT visible (prevents overlay on product image)
    this.atcInView = true;
    this.footerInView = false;
    this.mediaInView = false;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

    this.updateVisibility = () => {
      const blockOnMobileBecauseMedia = isMobile() && this.mediaInView;
      const shouldShow = !this.atcInView && !this.footerInView && !blockOnMobileBecauseMedia;

      this.classList.toggle('is-visible', shouldShow);
      this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };

    // 1) Observe main CTA button visibility
    this.ioATC = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.atcInView = !!(entry && entry.isIntersecting);
        this.updateVisibility();
      },
      { threshold: 0.15 }
    );
    this.ioATC.observe(this.mainSubmit);

    // 2) Observe footer visibility (hide sticky when footer comes into view)
    const footerEl =
      document.querySelector('footer') ||
      document.querySelector('.footer') ||
      document.querySelector('#shopify-section-footer');

    if (footerEl) {
      this.ioFooter = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.footerInView = !!(entry && entry.isIntersecting);
          this.updateVisibility();
        },
        { rootMargin: '0px 0px -35% 0px', threshold: 0.01 }
      );
      this.ioFooter.observe(footerEl);
    }

    // 3) MOBILE FIX: Observe product media/gallery visibility
    const mediaEl =
      document.querySelector('media-gallery') ||
      document.querySelector('.product__media-wrapper') ||
      document.querySelector('.product__media') ||
      document.querySelector('[data-media-gallery]');

    if (mediaEl) {
      this.ioMedia = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.mediaInView = !!(entry && entry.isIntersecting);
          this.updateVisibility();
        },
        { threshold: 0.15 }
      );
      this.ioMedia.observe(mediaEl);
    }

    // Re-evaluate on resize/orientation change
    this.onResize = () => this.updateVisibility();
    window.addEventListener('resize', this.onResize, { passive: true });

    // Initial state
    this.updateVisibility();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);

    if (this.submitBtn && this.onStickyClick) {
      this.submitBtn.removeEventListener('click', this.onStickyClick);
    }

    if (this.ioATC) this.ioATC.disconnect();
    if (this.ioFooter) this.ioFooter.disconnect();
    if (this.ioMedia) this.ioMedia.disconnect();

    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }

    if (this.bundleObserver) {
      this.bundleObserver.disconnect();
    }
  }

  formatMoney(cents) {
    // Prefer Shopify.formatMoney when available + use the money format passed via data attribute
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        // IMPORTANT: no Liquid-like placeholders here
        const moneyFormat = this.dataset.moneyFormat;
        if (moneyFormat) return Shopify.formatMoney(cents, moneyFormat);
      }
    } catch (e) {}

    // Fallback: EUR formatting
    return (cents / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR'
    });
  }
}

customElements.define('sticky-atc', StickyATC);